import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Divider, IconButton, SummaryInformationCategory, TCategory, Tooltip } from '@treezor/design-system';

import { useAccesControl } from '@/hooks/useAccessControl';

import { featuresSlugs } from '@/features/roles/store/models/features.model';
import { useProfileContext } from '@/features/context/ProfileContext';

export type TKycSection = { subTitle: string } & (
  | {
      component: ReactNode;
      categories?: never;
    }
  | {
      component?: never;
      categories: TCategory[];
    }
);

export interface IKycInformationSectionProps {
  section: TKycSection;
  isOnEdition: boolean;
  sectionKey: string;
  isLoading?: boolean;
  isFormInvalid?: boolean;
  isSectionNotEditable?: boolean;
  featureSlug?: keyof typeof featuresSlugs;
  onEdit?: () => void;
  onSave?: () => void;
  onCancel?: () => void;
}

export function KycInformationSection({
  section,
  isOnEdition,
  sectionKey: sectionIndex,
  isLoading,
  isFormInvalid,
  isSectionNotEditable,
  featureSlug = featuresSlugs.EDIT_INFORMATIONS,
  onEdit,
  onSave,
  onCancel,
}: IKycInformationSectionProps) {
  const { t } = useTranslation();
  const { hasAccessToFeature } = useAccesControl();
  const { user } = useProfileContext();

  return (
    <div className="flex gap-7" key={`section-${sectionIndex}`}>
      <div key={`wrap-subtitle-${sectionIndex}`} className="w-[148px] min-w-[148px]">
        <h6 className="text-secondary typo-h6-bold font-bold" key={`subtitle-${sectionIndex}`}>
          {section.subTitle}
        </h6>
      </div>

      {section.categories ? (
        <div className="flex flex-col gap-4 w-full" key={`section-wrapper-${sectionIndex}`}>
          {section.categories.map((category, categoriesIndex) => (
            <>
              <SummaryInformationCategory {...category} key={`category-${categoriesIndex}`} />
              {categoriesIndex !== section.categories.length - 1 && (
                <Divider thickness="200" key={`category-divider-${categoriesIndex}`} />
              )}
            </>
          ))}
        </div>
      ) : (
        <>{section.component} </>
      )}
      {user?.userStatus !== 'CANCELED' && hasAccessToFeature(featureSlug) && (
        <div className="ml-auto">
          {isOnEdition ? (
            <div className="flex gap-4 ml-auto">
              <Button
                variant="ghost"
                size="s"
                onClick={onCancel}
                disabled={!!isLoading}
                testId={`btn-cancel-${sectionIndex}`}
              >
                {t('pages.cores.cancel')}
              </Button>
              <Button
                size="s"
                onClick={onSave}
                isLoading={!!isLoading}
                disabled={isFormInvalid}
                testId={`btn-save-${sectionIndex}`}
              >
                {t('pages.cores.save')}
              </Button>
            </div>
          ) : isSectionNotEditable ? (
            <Tooltip
              messageContent={t('pages.currentProfiles.profile.tooltips.modificationInProgress')}
              contentClassName="w-[135px]"
            >
              <IconButton size="m" icon="edit" disabled testId={`icon-btn-kyc-edit-${sectionIndex}`} />
            </Tooltip>
          ) : (
            <IconButton size="m" icon="edit" onClick={onEdit} testId={`icon-btn-kyc-edit-${sectionIndex}`} />
          )}
        </div>
      )}
    </div>
  );
}
